import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, Calendar, List } from 'lucide-react';
import { toast } from 'sonner';
import { FormField } from './FormField';

// ---- Heatmap helpers ----
function inRecurringRange(monthDay, df, dt) {
  if (df <= dt) return df <= monthDay && monthDay <= dt;
  return monthDay >= df || monthDay <= dt;
}

function getRateInfo(dateStr, periods, exceptions) {
  const monthDay = dateStr.slice(5); // MM-DD
  const exc = exceptions.find(e => e.specific_date === dateStr);
  if (exc) return { price: exc.price_per_night, type: 'exception', label: 'Eccezione' };

  const sorted = [...periods].sort((a, b) => (b.price_per_night || 0) - (a.price_per_night || 0));
  // Recurring
  for (const p of sorted) {
    if (!p.is_recurring) continue;
    const df = p.date_from || '', dt = p.date_to || '';
    if (df.length === 5 && dt.length === 5 && inRecurringRange(monthDay, df, dt)) {
      return { price: p.price_per_night, type: p.period_type, label: p.name };
    }
  }
  // Fixed
  for (const p of sorted) {
    if (p.is_recurring) continue;
    if ((p.date_from || '') <= dateStr && dateStr <= (p.date_to || '')) {
      return { price: p.price_per_night, type: p.period_type, label: p.name };
    }
  }
  return null;
}

const TYPE_COLORS = {
  low:       { bg: 'bg-sky-900/50',    text: 'text-sky-300',    border: 'border-sky-800/60',    dot: '#38bdf8' },
  mid:       { bg: 'bg-amber-900/40',  text: 'text-amber-300',  border: 'border-amber-700/60',  dot: '#fbbf24' },
  high:      { bg: 'bg-orange-900/40', text: 'text-orange-300', border: 'border-orange-700/60', dot: '#fb923c' },
  holiday:   { bg: 'bg-red-900/40',    text: 'text-red-300',    border: 'border-red-800/60',    dot: '#f87171' },
  exception: { bg: 'bg-purple-900/40', text: 'text-purple-300', border: 'border-purple-800/60', dot: '#c084fc' },
};

const PERIOD_LABELS = { low: 'Bassa', mid: 'Media', high: 'Alta', holiday: 'Festività', exception: 'Eccezione' };
const DAY_NAMES = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];
const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

function HeatmapMonth({ year, month, periods, exceptions }) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // Monday-first offset
  const startDow = (firstDay.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div className="card-dark rounded-lg p-4">
      <p className="text-white text-sm font-medium mb-3 text-center">
        {MONTH_NAMES[month]} {year}
      </p>
      <div className="grid grid-cols-7 mb-1">
        {DAY_NAMES.map(d => (
          <div key={d} className="text-center text-[var(--lux-border-4)] text-[9px] py-1 uppercase">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const info = getRateInfo(dateStr, periods, exceptions);
          const colors = info ? (TYPE_COLORS[info.type] || TYPE_COLORS.mid) : null;
          const isToday = dateStr === todayStr;
          const isPast = dateStr < todayStr;

          return (
            <div
              key={dateStr}
              title={info ? `${info.label} — €${info.price}` : 'Nessuna tariffa'}
              className={`
                relative flex flex-col items-center justify-center rounded py-1.5 transition-all cursor-default
                ${info ? `${colors.bg} border ${colors.border}` : 'border border-[var(--lux-surface-2)]'}
                ${isPast ? 'opacity-40' : ''}
                ${isToday ? 'ring-1 ring-[#c9a84c] ring-offset-0' : ''}
              `}
            >
              <span className={`text-[10px] font-medium ${info ? colors.text : 'text-[var(--lux-border-3)]'}`}>{d}</span>
              {info && (
                <span className={`text-[8px] leading-none mt-0.5 ${colors.text} opacity-80`}>
                  {info.price > 0 ? `${Math.round(info.price)}` : '—'}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RateHeatmap({ periods, exceptions }) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const navMonth = (dir) => {
    let m = viewMonth + dir;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setViewMonth(m);
    setViewYear(y);
  };

  // Show 3 consecutive months
  const months = [0, 1, 2].map(offset => {
    let m = viewMonth + offset;
    let y = viewYear;
    if (m > 11) { m -= 12; y++; }
    return { year: y, month: m };
  });

  // Collect unique period types to show in legend
  const usedTypes = new Set();
  periods.forEach(p => usedTypes.add(p.period_type));
  if (exceptions.length > 0) usedTypes.add('exception');

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <p className="label-gold text-xs">Calendario Prezzi</p>
        <div className="flex items-center gap-2">
          <button onClick={() => navMonth(-3)} className="px-3 py-1.5 text-[var(--lux-text-muted-3)] hover:text-white text-xs border border-[#222] hover:border-[var(--lux-border-4)] rounded transition-colors">
            ← Prev
          </button>
          <span className="text-[var(--lux-text-muted)] text-xs px-2">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button onClick={() => navMonth(3)} className="px-3 py-1.5 text-[var(--lux-text-muted-3)] hover:text-white text-xs border border-[#222] hover:border-[var(--lux-border-4)] rounded transition-colors">
            Succ →
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {[...usedTypes].map(type => {
          const c = TYPE_COLORS[type] || TYPE_COLORS.mid;
          return (
            <div key={type} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: c.dot }} />
              <span className="text-[#777] text-xs">{PERIOD_LABELS[type] || type}</span>
            </div>
          );
        })}
        {usedTypes.size === 0 && (
          <p className="text-[var(--lux-border-4)] text-xs">Nessuna tariffa configurata — aggiungi periodi tariffari</p>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="text-[var(--lux-border-4)] text-[10px]">Passa il mouse su una cella per il dettaglio</span>
        </div>
      </div>

      {/* 3 months grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {months.map(({ year, month }) => (
          <HeatmapMonth key={`${year}-${month}`} year={year} month={month}
            periods={periods} exceptions={exceptions} />
        ))}
      </div>
    </div>
  );
}

export default function Rates() {
  const [properties, setProperties] = useState([]);
  const [selectedPropId, setSelectedPropId] = useState('');
  const [periods, setPeriods] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelType, setPanelType] = useState('period');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [view, setView] = useState('list'); // 'list' | 'heatmap'

  useEffect(() => {
    api.get('/properties').then(r => {
      setProperties(r.data);
      if (r.data.length > 0) setSelectedPropId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!selectedPropId) return;
    setLoading(true);
    try {
      const res = await api.get(`/rates?property_id=${selectedPropId}`);
      setPeriods(res.data.periods || []);
      setExceptions(res.data.exceptions || []);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [selectedPropId]);

  useEffect(() => { load(); }, [load]);

  const openCreatePeriod = () => {
    setPanelType('period');
    setEditing(null);
    setForm({ property_id: selectedPropId, name: '', period_type: 'mid', date_from: '', date_to: '', is_recurring: false, price_per_night: 0, weekend_surcharge: 0 });
    setPanelOpen(true);
  };

  const openEditPeriod = (p) => {
    setPanelType('period');
    setEditing(p.id);
    setForm({ ...p, property_id: selectedPropId });
    setPanelOpen(true);
  };

  const openCreateException = () => {
    setPanelType('exception');
    setEditing(null);
    setForm({ property_id: selectedPropId, specific_date: '', price_per_night: 0 });
    setPanelOpen(true);
  };

  const save = async () => {
    try {
      if (panelType === 'period') {
        if (editing) {
          await api.put(`/rates/periods/${editing}`, form);
          toast.success('Tariffa aggiornata');
        } else {
          await api.post('/rates/periods', form);
          toast.success('Tariffa creata');
        }
      } else {
        await api.post('/rates/exceptions', form);
        toast.success('Eccezione creata');
      }
      setPanelOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const deletePeriod = async (id) => {
    if (!confirm('Eliminare questa tariffa?')) return;
    await api.delete(`/rates/periods/${id}`);
    load();
  };

  const deleteException = async (id) => {
    await api.delete(`/rates/exceptions/${id}`);
    load();
  };

  const PERIOD_TYPES = [
    { value: 'low', label: 'Bassa stagione' }, { value: 'mid', label: 'Media stagione' },
    { value: 'high', label: 'Alta stagione' }, { value: 'holiday', label: 'Festività' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Tariffe</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* View toggle */}
          <div className="flex border border-[#222] rounded overflow-hidden">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors ${view === 'list' ? 'bg-[var(--lux-border-1)] text-white' : 'text-[var(--lux-text-muted-3)] hover:text-white'}`}
              data-testid="view-list-btn"
            >
              <List size={12} /> Lista
            </button>
            <button
              onClick={() => setView('heatmap')}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors ${view === 'heatmap' ? 'bg-[var(--lux-border-1)] text-white' : 'text-[var(--lux-text-muted-3)] hover:text-white'}`}
              data-testid="view-heatmap-btn"
            >
              <Calendar size={12} /> Calendario
            </button>
          </div>
          <button onClick={openCreatePeriod} className="btn-gold flex items-center gap-2 text-xs" data-testid="new-rate-btn">
            <Plus size={14} /> Nuova Tariffa
          </button>
          <button onClick={openCreateException} className="btn-outline-gold flex items-center gap-2 text-xs" data-testid="new-exception-btn">
            <Plus size={14} /> Eccezione Data
          </button>
        </div>
      </div>

      <select className="input-dark max-w-xs" value={selectedPropId} onChange={e => setSelectedPropId(e.target.value)}>
        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {loading ? <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div> : (
        view === 'heatmap' ? (
          <RateHeatmap periods={periods} exceptions={exceptions} />
        ) : (
          <>
            <div className="card-dark rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-[var(--lux-border-1)]">
                <p className="label-gold text-xs">Periodi Tariffari</p>
              </div>
              {periods.length === 0 ? (
                <p className="text-[var(--lux-text-muted-3)] text-sm p-6">Nessuna tariffa configurata.</p>
              ) : (
                <table className="w-full table-dark">
                  <thead><tr>
                    <th className="text-left">Nome</th>
                    <th className="text-left">Tipo</th>
                    <th className="text-left hidden sm:table-cell">Da</th>
                    <th className="text-left hidden sm:table-cell">A</th>
                    <th className="text-right">€/notte</th>
                    <th className="text-right hidden md:table-cell">Suppl. wknd</th>
                    <th />
                  </tr></thead>
                  <tbody>
                    {periods.map(p => {
                      const dot = TYPE_COLORS[p.period_type]?.dot || '#888';
                      return (
                        <tr key={p.id}>
                          <td className="text-white">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} />
                              {p.name}
                            </div>
                          </td>
                          <td className="text-[var(--lux-text-muted-2)] text-xs capitalize">{PERIOD_LABELS[p.period_type] || p.period_type}{p.is_recurring ? ' ♻' : ''}</td>
                          <td className="hidden sm:table-cell text-[var(--lux-text-sec)] text-xs">{p.date_from}</td>
                          <td className="hidden sm:table-cell text-[var(--lux-text-sec)] text-xs">{p.date_to}</td>
                          <td className="text-right text-[#c9a84c]">€{p.price_per_night}</td>
                          <td className="text-right hidden md:table-cell text-[var(--lux-text-muted-2)] text-xs">+€{p.weekend_surcharge || 0}</td>
                          <td>
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => openEditPeriod(p)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-white"><Edit size={13} /></button>
                              <button onClick={() => deletePeriod(p.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="card-dark rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-[var(--lux-border-1)]"><p className="label-gold text-xs">Eccezioni per Data</p></div>
              {exceptions.length === 0 ? (
                <p className="text-[var(--lux-text-muted-3)] text-sm p-6">Nessuna eccezione.</p>
              ) : (
                <table className="w-full table-dark">
                  <thead><tr>
                    <th className="text-left">Data</th>
                    <th className="text-right">€/notte</th>
                    <th />
                  </tr></thead>
                  <tbody>
                    {exceptions.map(e => (
                      <tr key={e.id}>
                        <td className="text-white">{e.specific_date}</td>
                        <td className="text-right text-[#c9a84c]">€{e.price_per_night}</td>
                        <td>
                          <button onClick={() => deleteException(e.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400 float-right"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )
      )}

      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">
              {panelType === 'period' ? (editing ? 'Modifica Tariffa' : 'Nuova Tariffa') : 'Eccezione Data'}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            {panelType === 'period' && (
              <>
                <FormField label="Nome" name="name" form={form} setForm={setForm} />
                <FormField label="Tipo" name="period_type" form={form} setForm={setForm} options={PERIOD_TYPES} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Data inizio (YYYY-MM-DD o MM-DD)" name="date_from" form={form} setForm={setForm} />
                  <FormField label="Data fine" name="date_to" form={form} setForm={setForm} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="€/notte" name="price_per_night" type="number" form={form} setForm={setForm} />
                  <FormField label="Suppl. weekend" name="weekend_surcharge" type="number" form={form} setForm={setForm} />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.is_recurring || false} onChange={e => setForm(p => ({ ...p, is_recurring: e.target.checked }))} className="accent-[#c9a84c]" />
                  <span className="text-sm text-[var(--lux-text-sec)]">Ricorrente annuale</span>
                </label>
              </>
            )}
            {panelType === 'exception' && (
              <>
                <FormField label="Data (YYYY-MM-DD)" name="specific_date" form={form} setForm={setForm} />
                <FormField label="€/notte" name="price_per_night" type="number" form={form} setForm={setForm} />
              </>
            )}
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} className="btn-gold flex-1">Salva</button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
